import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import { useJournalEntry } from '../../hooks/useJournalEntry';

const JournalEntryModal = ({ show, onHide, activeProject, accounts, fetchTransactions, editingId }) => {
  const { createJournalEntry, updateJournalEntry, getJournalEntry, deleteJournalEntry, loading, error, setError } = useJournalEntry(activeProject);

  const initialLine = { accountId: '', description: '', debit: '', credit: '' };
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    reference: '',
    lines: [{ ...initialLine }, { ...initialLine }]
  });

  const [totals, setTotals] = useState({ debit: 0, credit: 0 });
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show && editingId) {
        const fetchEntry = async () => {
            const data = await getJournalEntry(editingId);
            if (data) {
                setFormData({
                    date: new Date(data.date).toISOString().split('T')[0],
                    description: data.description || '',
                    reference: data.reference || '',
                    lines: data.lines.map(l => ({
                        accountId: l.accountId?._id || l.accountId,
                        description: l.description || '',
                        debit: l.debit || '',
                        credit: l.credit || ''
                    }))
                });
            }
        };
        fetchEntry();
    } else if (show && !editingId) {
        setFormData({
            date: new Date().toISOString().split('T')[0],
            description: '',
            reference: '',
            lines: [{ ...initialLine }, { ...initialLine }]
        });
        setError(null);
        setValidated(false);
    }
  }, [show, editingId]);

  useEffect(() => {
    const debit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
    const credit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
    setTotals({ debit, credit });
  }, [formData.lines]);

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    // If user enters debit, clear credit and vice versa
    if (field === 'debit' && value) newLines[index].credit = '';
    if (field === 'credit' && value) newLines[index].debit = '';

    setFormData({ ...formData, lines: newLines });
  };

  const addLine = () => {
    setFormData({ ...formData, lines: [...formData.lines, { ...initialLine }] });
  };

  const removeLine = (index) => {
    if (formData.lines.length <= 2) return;
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData({ ...formData, lines: newLines });
  };

  const handleDelete = async () => {
    const success = await deleteJournalEntry(editingId);
    if (success) {
        fetchTransactions();
        onHide();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    // Memo validation
    if (!formData.description) {
        setError('A memo/description is required for the journal entry.');
        return;
    }
    
    // Basic validation
    if (Math.abs(totals.debit - totals.credit) > 0.001) {
        setError('Journal entry is not balanced. Debits must equal Credits.');
        return;
    }

    if (formData.lines.some(l => !l.accountId || (!l.debit && !l.credit))) {
        setError('Each line must have an account and either a debit or credit amount.');
        return;
    }

    const success = editingId 
        ? await updateJournalEntry(editingId, formData)
        : await createJournalEntry(formData);

    if (success) {
      fetchTransactions();
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{editingId ? 'Edit Journal Entry' : 'New Journal Entry'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          
          <div className="row mb-3">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label className="small font-weight-bold">Journal Date</Form.Label>
                <Form.Control type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required size="sm" />
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label className="small font-weight-bold">Journal no.</Form.Label>
                <Form.Control type="text" value={formData.reference} onChange={e => setFormData({...formData, reference: e.target.value})} placeholder="Optional" size="sm" />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="small font-weight-bold">Memo</Form.Label>
                <Form.Control 
                    type="text" 
                    value={formData.description} 
                    onChange={e => setFormData({...formData, description: e.target.value})} 
                    placeholder="Overall description" 
                    size="sm" 
                    isInvalid={validated && !formData.description}
                    required
                />
                <Form.Control.Feedback type="invalid">
                    Please provide a memo/description.
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>

          <Table bordered size="sm">
            <thead className="bg-light">
              <tr>
                <th style={{ width: '30%' }}>Account</th>
                <th style={{ width: '15%' }}>Debits</th>
                <th style={{ width: '15%' }}>Credits</th>
                <th style={{ width: '30%' }}>Description</th>
                <th style={{ width: '5%' }}></th>
              </tr>
            </thead>
            <tbody>
              {formData.lines.map((line, index) => (
                <tr key={index}>
                  <td>
                    <Form.Control as="select" value={line.accountId} onChange={e => handleLineChange(index, 'accountId', e.target.value)} required size="sm">
                      <option value="">Select account</option>
                      {accounts.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
                    </Form.Control>
                  </td>
                  <td>
                    <Form.Control type="number" step="0.01" value={line.debit} onChange={e => handleLineChange(index, 'debit', e.target.value)} placeholder="0.00" size="sm" />
                  </td>
                  <td>
                    <Form.Control type="number" step="0.01" value={line.credit} onChange={e => handleLineChange(index, 'credit', e.target.value)} placeholder="0.00" size="sm" />
                  </td>
                  <td>
                    <Form.Control type="text" value={line.description} onChange={e => handleLineChange(index, 'description', e.target.value)} placeholder="Line description" size="sm" />
                  </td>
                  <td className="text-center">
                    <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => removeLine(index)}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-light font-weight-bold">
                <td>Total</td>
                <td className="text-right">${totals.debit.toFixed(2)}</td>
                <td className="text-right">${totals.credit.toFixed(2)}</td>
                <td colSpan="2">
                  {Math.abs(totals.debit - totals.credit) > 0.001 && (
                    <span className="text-danger small ml-2">Out of balance: ${(totals.debit - totals.credit).toFixed(2)}</span>
                  )}
                </td>
              </tr>
            </tfoot>
          </Table>

          <Button variant="outline-primary" size="sm" onClick={addLine}>
            <i className="fas fa-plus mr-1"></i> Add lines
          </Button>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {editingId && (
                <Button variant="danger" size="sm" onClick={handleDelete} disabled={loading}>
                    Delete Entry
                </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="mr-2">Cancel</Button>
            <Button variant="primary" type="submit" disabled={loading || Math.abs(totals.debit - totals.credit) > 0.001}>
              {loading ? 'Saving...' : (editingId ? 'Update Journal Entry' : 'Save Journal Entry')}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default JournalEntryModal;
